// Reusable dialog for creating/managing a shareable read-only link for a set of
// plant scans. Owner picks title + view mode (brief/full) + expiration, then the
// dialog surfaces Copy Link / Email / Share PDF actions.
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Copy, Mail, FileDown, Loader2, Share2, Check, RefreshCcw } from "lucide-react";
import { shares as sharesApi, plants as plantsApi } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useMilestonesContext } from "@/contexts/MilestonesContext";
import { exportPlantsPdf } from "@/lib/pdfExport";
import { canShareFiles, downloadBlob, copyToClipboard, mailtoShareUrl } from "@/lib/webShare";

const EXPIRY_OPTIONS = [
  { value: "7",   labelKey: "share.expiry.7d" },
  { value: "30",  labelKey: "share.expiry.30d" },
  { value: "0",   labelKey: "share.expiry.never" }, // 0 means never
];

// Format an absolute date/time using the user's locale.
const formatExpiry = (iso, lang) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(lang === "es" ? "es-ES" : "en-US", {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch { return null; }
};

export default function ShareDialog({ open, onOpenChange, selectedIds = [], allScanCount = 0, initialScope = "all" }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { checkNow: checkMilestones } = useMilestonesContext();

  const [title, setTitle] = useState("");
  const [viewMode, setViewMode] = useState("full");
  const [expiry, setExpiry] = useState("30");
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState(null); // {share_url, share_id, expires_at, ...}
  const [copied, setCopied] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);

  // Scope of what to share: "all" or "selected". Defaults to whatever the
  // opener signalled; users can flip it inside the dialog. If the user
  // opened the dialog with no selections, "selected" is unavailable.
  const hasSelection = Array.isArray(selectedIds) && selectedIds.length > 0;
  const [scope, setScope] = useState(hasSelection && initialScope === "selected" ? "selected" : "all");

  // Keep `scope` in sync whenever the dialog is (re)opened with new inputs.
  React.useEffect(() => {
    if (!open) return;
    if (initialScope === "selected" && hasSelection) setScope("selected");
    else setScope("all");
  }, [open, initialScope, hasSelection]);

  const selectedCount = selectedIds?.length || 0;
  const shareCount = scope === "selected" ? selectedCount : allScanCount;

  const create = async () => {
    setCreating(true);
    try {
      const days = expiry === "0" ? null : parseInt(expiry, 10);
      const payload = {
        title: title.trim() || null,
        view_mode: viewMode,
        expires_in_days: days,
      };
      if (scope === "selected" && hasSelection) {
        payload.scan_ids = selectedIds;
      }
      const { data } = await sharesApi.create(payload);
      // Backend returns share_url relative if PUBLIC_APP_URL isn't set — always turn it
      // into an absolute URL from the browser's own origin so recipients get a working link.
      const absoluteUrl = /^https?:\/\//i.test(data.share_url)
        ? data.share_url
        : `${window.location.origin}${data.share_url}`;
      setResult({ ...data, share_url: absoluteUrl });
      // Sharing is itself a conservation milestone ("First list shared") —
      // fire the check so the celebratory sheet slides in behind the modal.
      // Fire-and-forget: don't block the copy-link flow on it.
      checkMilestones();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("share.errorCreate"));
    } finally {
      setCreating(false);
    }
  };

  const reset = () => {
    setResult(null);
    setCopied(false);
    setTitle("");
    setViewMode("full");
    setExpiry("30");
  };

  const doCopy = async () => {
    if (!result?.share_url) return;
    const ok = await copyToClipboard(result.share_url);
    if (ok) {
      setCopied(true);
      toast.success(t("share.copied"));
      setTimeout(() => setCopied(false), 1600);
    } else {
      toast.error(t("share.copyFail"));
    }
  };

  const emailSubject = useMemo(
    () => t("share.emailSubject", { name: user?.name || "" }),
    [user, t],
  );
  const emailBody = useMemo(
    () => {
      if (!result) return "";
      const lines = [
        t("share.emailBodyLine1", { name: user?.name || "" }),
        "",
        result.share_url,
      ];
      const exp = formatExpiry(result.expires_at, user?.language);
      if (exp) lines.push("", t("share.emailBodyExpires", { date: exp }));
      return lines.join("\n");
    },
    [result, user, t],
  );

  const doEmail = useCallback(() => {
    if (!result) return;
    // Fire the mailto: navigation. This works when the OS has a default
    // mail handler (typical on mobile / desktops with Outlook/Thunderbird
    // /a browser-registered handler like Gmail). Desktop browsers with
    // NO registered handler will silently no-op — that's the bug we
    // reported on Prod. Regardless of outcome we copy the message body
    // to the clipboard and surface a toast so the user always has a
    // paste-ready fallback.
    try {
      window.location.href = mailtoShareUrl({ subject: emailSubject, body: emailBody });
    } catch {
      // Any navigation error still leaves us with the clipboard fallback below.
    }
    (async () => {
      const combined = `${emailSubject}\n\n${emailBody}`;
      const ok = await copyToClipboard(combined);
      if (ok) toast.message(t("share.emailFallbackCopied"));
      else toast.error(t("share.emailFallbackCopyFail"));
    })();
  }, [result, emailSubject, emailBody, t]);

  const doPdf = async () => {
    if (!result) return;
    setPdfBusy(true);
    try {
      // Fetch full item payload for the shared scans so the PDF has images.
      const { data } = await plantsApi.list();
      const all = data?.items || [];
      const idSet = new Set(result.scan_ids || []);
      const items = idSet.size ? all.filter((i) => idSet.has(i.scan_id)) : all;
      if (items.length === 0) {
        toast.message(t("share.pdfEmpty"));
        return;
      }
      const filename = `prairie_share_${result.share_id}.pdf`;
      const out = await exportPlantsPdf(items, {
        language: user?.language || "en",
        state: user?.state || "",
        userName: user?.name,
        t,
        shareUrl: result.share_url,
        output: canShareFiles() ? "blob" : "save",
        filename,
      });
      if (out?.blob) {
        const file = new File([out.blob], filename, { type: "application/pdf" });
        try {
          await navigator.share({
            title: t("share.pdfShareTitle"),
            text: t("share.pdfShareText", { url: result.share_url }),
            files: [file],
          });
        } catch (err) {
          if (err?.name !== "AbortError") {
            // Fallback to download when share sheet is unavailable/dismissed with error.
            downloadBlob(out.blob, filename);
          }
        }
      }
    } catch {
      toast.error(t("share.pdfFail"));
    } finally {
      setPdfBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) reset();
      }}
    >
      <DialogContent className="max-w-md" data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl" data-testid="share-dialog-title">
            {result ? t("share.readyTitle") : t("share.setupTitle")}
          </DialogTitle>
          <DialogDescription>
            {result
              ? t("share.readyDesc", { count: result.scan_ids?.length || 0 })
              : t("share.setupDescCount", { n: shareCount })}
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <div className="space-y-4">
            {/* Scope selector — only shown when a "selected" alternative is
                actually available, so users who haven't picked anything don't
                see a disabled option they can't reason about. */}
            {hasSelection && (
              <div data-testid="share-scope-row">
                <p className="text-xs uppercase tracking-[0.14em] text-leaf-mist mb-1.5">
                  {t("share.scopeLabel")}
                </p>
                <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label={t("share.scopeLabel")}>
                  {[
                    { key: "all", label: t("share.scopeAll", { n: allScanCount }) },
                    { key: "selected", label: t("share.scopeSelected", { n: selectedCount }) },
                  ].map((opt) => {
                    const active = scope === opt.key;
                    return (
                      <button
                        key={opt.key}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        onClick={() => setScope(opt.key)}
                        data-testid={`share-scope-${opt.key}`}
                        className={`rounded-2xl px-3 py-2.5 text-sm border transition ${
                          active
                            ? "bg-leaf/5 border-leaf text-leaf-ink"
                            : "bg-white border-[#E8E5DF] text-leaf-ink hover:bg-parchment"
                        }`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div>
              <label className="block text-xs uppercase tracking-[0.14em] text-leaf-mist mb-1.5">
                {t("share.titleLabel")}
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value.slice(0, 120))}
                placeholder={t("share.titlePlaceholder")}
                maxLength={120}
                data-testid="share-title-input"
                className="w-full rounded-2xl border border-[#E8E5DF] bg-cream px-4 py-3 text-sm text-leaf-ink focus:outline-none focus:ring-2 focus:ring-leaf/40"
              />
            </div>

            <div>
              <p className="text-xs uppercase tracking-[0.14em] text-leaf-mist mb-1.5">
                {t("share.detailLabel")}
              </p>
              <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label={t("share.detailLabel")}>
                {["full", "brief"].map((m) => {
                  const active = viewMode === m;
                  return (
                    <button
                      key={m}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      onClick={() => setViewMode(m)}
                      data-testid={`share-mode-${m}`}
                      className={`rounded-2xl px-3 py-3 text-left border transition ${
                        active
                          ? "bg-leaf/5 border-leaf text-leaf-ink"
                          : "bg-white border-[#E8E5DF] text-leaf-ink hover:bg-parchment"
                      }`}
                    >
                      <p className="font-medium text-sm">{t(`share.detail.${m}Label`)}</p>
                      <p className="mt-1 text-[11px] text-leaf-mist leading-snug">
                        {t(`share.detail.${m}Desc`)}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <p className="text-xs uppercase tracking-[0.14em] text-leaf-mist mb-1.5">
                {t("share.expiryLabel")}
              </p>
              <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label={t("share.expiryLabel")}>
                {EXPIRY_OPTIONS.map((opt) => {
                  const active = expiry === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      onClick={() => setExpiry(opt.value)}
                      data-testid={`share-expiry-${opt.value}`}
                      className={`rounded-2xl py-3 text-sm transition border ${
                        active
                          ? "bg-leaf text-white border-leaf"
                          : "bg-white text-leaf-ink border-[#E8E5DF] hover:bg-parchment"
                      }`}
                    >
                      {t(opt.labelKey)}
                    </button>
                  );
                })}
              </div>
            </div>

            <DialogFooter className="sm:justify-between gap-2">
              <button
                type="button"
                onClick={() => { reset(); onOpenChange(false); }}
                data-testid="share-cancel"
                className="rounded-full px-5 py-2.5 text-sm bg-white border border-[#E8E5DF] text-leaf-ink hover:bg-parchment transition"
                disabled={creating}
              >
                {t("common.cancel")}
              </button>
              <button
                type="button"
                onClick={create}
                data-testid="share-create"
                disabled={creating}
                className="rounded-full px-5 py-2.5 text-sm bg-leaf text-white hover:bg-leaf-deep transition inline-flex items-center gap-2 disabled:opacity-70"
              >
                {creating
                  ? <><Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> {t("share.creating")}</>
                  : <><Share2 className="w-4 h-4" strokeWidth={1.5} /> {t("share.create")}</>}
              </button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-parchment rounded-2xl p-3 border border-[#E8E5DF] break-all text-[12px] text-leaf-ink" data-testid="share-url">
              {result.share_url}
            </div>
            {result.expires_at && (
              <p className="text-[11px] text-leaf-mist -mt-2" data-testid="share-expires">
                {t("share.expiresOn", { date: formatExpiry(result.expires_at, user?.language) })}
              </p>
            )}

            <div className="grid grid-cols-1 gap-2">
              <button
                type="button"
                onClick={doCopy}
                data-testid="share-copy"
                className="rounded-full px-4 py-3 bg-leaf text-white hover:bg-leaf-deep transition inline-flex items-center justify-center gap-2"
              >
                {copied
                  ? <><Check className="w-4 h-4" strokeWidth={1.5} /> {t("share.copied")}</>
                  : <><Copy className="w-4 h-4" strokeWidth={1.5} /> {t("share.copy")}</>}
              </button>
              <button
                type="button"
                onClick={doEmail}
                data-testid="share-email"
                className="rounded-full px-4 py-3 bg-white border border-[#E8E5DF] text-leaf-ink hover:bg-parchment transition inline-flex items-center justify-center gap-2"
              >
                <Mail className="w-4 h-4" strokeWidth={1.5} /> {t("share.email")}
              </button>
              <button
                type="button"
                onClick={doPdf}
                data-testid="share-pdf"
                disabled={pdfBusy}
                className="rounded-full px-4 py-3 bg-white border border-[#E8E5DF] text-leaf-ink hover:bg-parchment transition inline-flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {pdfBusy
                  ? <><Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> {t("share.pdfBusy")}</>
                  : <><FileDown className="w-4 h-4" strokeWidth={1.5} /> {t("share.pdf")}</>}
              </button>
            </div>

            <DialogFooter className="sm:justify-between gap-2">
              <button
                type="button"
                onClick={reset}
                data-testid="share-new"
                className="rounded-full px-5 py-2.5 text-sm bg-white border border-[#E8E5DF] text-leaf-ink hover:bg-parchment transition inline-flex items-center gap-2"
              >
                <RefreshCcw className="w-4 h-4" strokeWidth={1.5} /> {t("share.newAnother")}
              </button>
              <button
                type="button"
                onClick={() => { reset(); onOpenChange(false); }}
                data-testid="share-done"
                className="rounded-full px-5 py-2.5 text-sm bg-leaf text-white hover:bg-leaf-deep transition"
              >
                {t("share.done")}
              </button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
